<script setup lang="ts" generic="t">
import { PsIcon as Icon, PsButton as Button } from 'ps-ui';
import PlanroomTree from './PlanroomTree.vue';
import PlanroomFile from './PlanroomFile.vue';
import PlanroomFolder from './PlanroomFolder.vue';
import {ref, watch} from 'vue';
withDefaults(
  defineProps<{
    files: t[];
    title?: string;
    downloadAllFiles?: () => unknown;
    downloadSelectedFiles?: (files: t[]) => unknown;
    downloadFile?: (file: t) => unknown;
    selectTools: boolean;
    expandTools: boolean;
    renameFile?: (file: t, updatedName: string) => Promise<unknown>;
    deleteFile?: (file: t) => Promise<unknown>;
    defaultExpandedState?: boolean;
  }>(),
  {
    selectTools: true,
    expandTools: true,
    defaultExpandedState: false,
  },
);

const selectedFiles = defineModel<t[]>('selectedFiles', { default: [] });
const fileTree = ref();

const emit = defineEmits<{
  'update:selectedFiles': [t[]];
}>();

watch(selectedFiles, (a, b) => {
  if (a.length === 0 && b.length === 0) return;
  emit('update:selectedFiles', selectedFiles.value);
});
</script>
<template>
  <div class="ps-file-tree ps-ui-element">
    <div v-if="title || $slots.title" class="file-tree-title">
      <slot name="title">
        <h3>{{ title }}</h3>
      </slot>
    </div>
    <div
      v-if="
        downloadAllFiles ||
        downloadSelectedFiles ||
        $slots.additionalTools ||
        selectTools ||
        expandTools
      "
      class="file-tree-tools"
    >
      <div
        v-if="
          downloadAllFiles || downloadSelectedFiles || $slots.additionalTools
        "
        class="file-tree-tools-row"
      >
        <div
          v-if="downloadAllFiles || downloadSelectedFiles"
          class="file-tree-toolset"
        >
          <Button
            v-if="downloadSelectedFiles"
            class="tree-tool is-text"
            @click="downloadSelectedFiles(selectedFiles)"
          >
            <Icon icon="download" />
            <span>Download Selected Files</span>
          </Button>
          <Button
            v-if="downloadAllFiles"
            class="tree-tool is-text"
            @click="downloadAllFiles()"
          >
            <Icon icon="download" />
            <span>Download All Files</span>
          </Button>
        </div>
        <div v-if="$slots.additionalTools" class="file-tree-toolset">
          <slot name="additionalTools"></slot>
        </div>
      </div>
      <div v-if="selectTools || expandTools" class="file-tree-tools-row">
        <div v-if="selectTools" class="file-tree-toolset">
          <Button
            class="tree-tool is-text"
            @click="fileTree?.setAllItemsSelected(true)"
          >
            <Icon icon="square-check" />
            <span>Select All</span>
          </Button>
          <Button
            class="tree-tool is-text"
            @click="fileTree?.setAllItemsSelected(false)"
          >
            <Icon icon="square" />
            <span>Deselect All</span>
          </Button>
        </div>
        <div
          v-if="expandTools && fileTree?.hasParents"
          class="file-tree-toolset"
        >
          <Button
            class="tree-tool is-text"
            @click="fileTree?.setAllExpanded(true)"
          >
            <Icon icon="square-plus" />
            <span>Expand All</span>
          </Button>
          <Button
            class="tree-tool is-text"
            @click="fileTree?.setAllExpanded(false)"
          >
            <Icon icon="square-minus" />
            <span>Collapse All</span>
          </Button>
        </div>
      </div>
    </div>
    <PlanroomTree
      ref="fileTree"
      :items="files"
      item-key="id"
      children-key="files"
      :selectable="selectTools"
      :collapseable="false"
      :default-expanded-state="defaultExpandedState"
      :selected-items="selectedFiles"
      @update:selected-items="(value: t[]) => (selectedFiles = value)"
    >
      <template #item="{ item, expanded }">
        <PlanroomFolder
          v-if="item.folder"
          :name="item.name"
          :open="expanded"
        />
        <PlanroomFile
          v-else
          :name="item.name"
          :type="item.extension ?? item.type"
          :uploaded-at="item.created_at ? new Date(item.created_at) : undefined"
          :size="item.size"
          :link="downloadFile ? undefined : item.url"
          :clickable="Boolean(downloadFile)"
          @click="downloadFile?.(item)"
        />
      </template>
    </PlanroomTree>
  </div>
</template>
<style scoped>
.ps-file-tree {
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem 0.5rem;
  /* background-color: color-mix(in srgb, var(--ps-ui-c-grey-400) 15%, transparent);*/

  :deep(.planroom-tree) {
    padding: 0.25rem;
    border-radius: 0.25rem;
    /*background-color: color-mix(in srgb, var(--ps-ui-c-white) 50%, transparent);*/
  }

  .file-tree-tools-row {
    display: flex;
    flex-wrap: wrap;
  }
}

:deep(.ps-file) {
  align-items: center;
  gap: 0.35rem;
}

:deep(.ps-file .file-type) {
  align-items: center;
  box-sizing: border-box;
  display: inline-flex;
  flex: 0 0 1.25rem;
  height: 1.25rem;
  justify-content: center;
  padding-top: 0 !important;
  width: 1.25rem;
}

:deep(.ps-folder .folder-icon) {
  align-items: center;
  box-sizing: border-box;
  display: inline-flex;
  flex: 0 0 1.25rem;
  height: 1.25rem;
  justify-content: center;
  width: 1.25rem;
}

:deep(.ps-folder .folder-icon > span),
:deep(.ps-file .file-type > span) {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

:deep(.ps-folder .folder-icon svg),
:deep(.ps-file .file-type svg) {
  display: block;
  margin: 0;
}

:deep(.ps-folder .folder-title) {
  gap: 0.35rem;
}

:deep(.ps-file .file-icon) {
  line-height: 1;
}

:deep(.ps-file .file-title-tools-meta) {
  align-items: center;
  flex-direction: row !important;
  gap: 0.5rem;
}

:deep(.ps-file .file-meta) {
  align-items: center;
  display: inline-flex;
  font-size: 0.75rem;
  gap: 0.25rem;
  line-height: normal;
  white-space: nowrap;
}

@media screen and (max-width: 768px) {
  :deep(.ps-file .file-title-tools-meta) {
    align-items: flex-start;
    flex-direction: column;
    gap: 0;
  }
}

.file-tree-tools {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  padding-block: 0.5rem;
}

.file-tree-toolset {
  display: inline-flex;
  flex-wrap: wrap;
}

.tree-tool {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  text-align: left;
}
</style>
